import { Api, ChatContextItem, ChatMessage, ChatSession } from "../api";
import { itemPathSeparator } from "../data/types";
import { Tiktoken } from "js-tiktoken/lite";
// @ts-ignore
import cl100kBase from "../data/cl100k_base.json";
const enc = new Tiktoken(cl100kBase);
const numTokens = (message: string) => enc.encode(message).length;

/*export function numTokens(text: string): number {
    return Math.ceil(text.length / 3);
}*/

export class ChatState {
    private static instance: ChatState;
    private listeners: Set<(state: ChatState) => void> = new Set();

    private _session: ChatSession = {
        id: null,
        messages: [],
        context: [],
        lastModified: 0,
    };

    private _tokenUsage = {
        inputTokens: 0,
        outputTokens: 0,
    };

    private _cachedTotalTokens: number | null = null;

    get tokenUsage() {
        return this._tokenUsage;
    }

    static getInstance(): ChatState {
        if (!ChatState.instance) {
            ChatState.instance = new ChatState();
        }
        return ChatState.instance;
    }

    get session() {
        return this._session;
    }

    subscribe(listener: (state: ChatState) => void) {
        this.listeners.add(listener);
        listener(this);
        return () => this.listeners.delete(listener);
    }

    notifyListeners() {
        this.listeners.forEach((listener) => listener(this));
    }

    newSession() {
        this._session = {
            id: null,
            messages: [],
            context: [],
            lastModified: 0,
        };
        this._tokenUsage = {
            inputTokens: 0,
            outputTokens: 0,
        };
        this._cachedTotalTokens = 0;
        this.notifyListeners();
    }

    public async addContextItems(items: ChatContextItem[]): Promise<number> {
        if (!this.session) return 0;

        // Calculate tokens for each new item
        const itemTokens = items.map((item) => {
            const json = this.createContextItemJson(item, this.session.context.length);
            return numTokens(json);
        });

        // Find how many items we can add while staying under 120k tokens
        let totalTokens = this._cachedTotalTokens || 0;
        let itemsToAdd = 0;

        for (let i = 0; i < items.length; i++) {
            if (totalTokens + itemTokens[i] > 120000) {
                break;
            }
            totalTokens += itemTokens[i];
            itemsToAdd++;
        }

        // Add the items we can fit
        this.session.context.push(...items.slice(0, itemsToAdd));
        this._cachedTotalTokens = null; // Invalidate cache
        this.notifyListeners();

        return itemsToAdd;
    }

    private createContextItemJson(ctx: ChatContextItem, id: number): string {
        const mediaType = ctx.item.path.split(itemPathSeparator)[0];

        if (ctx.type == "segment") {
            return JSON.stringify({
                id,
                type: "segment",
                mediaType,
                sourceTitle: ctx.item.title,
                date: new Date(ctx.item.date).toISOString().split("T")[0],
                title: ctx.segment.title,
                text: ctx.segment.text,
                subtitles:
                    ctx.segment.subtitles?.map((s, index) => ({
                        index,
                        text: s.speaker ? `${s.speaker}: ${s.text}` : s.text,
                    })) || [],
            });
        } else {
            return JSON.stringify({
                id,
                type: "complete",
                mediaType,
                title: ctx.item.title,
                date: new Date(ctx.item.date).toISOString().split("T")[0],
                text: ctx.item.text,
                segments:
                    ctx.item.segments?.map((seg, index) => ({
                        index,
                        title: seg.title,
                        text: seg.text,
                        subtitles:
                            seg.subtitles?.map((s, subtitleIndex) => ({
                                index: subtitleIndex,
                                text: s.speaker ? `${s.speaker}: ${s.text}` : s.text,
                            })) || [],
                    })) || [],
            });
        }
    }

    static createSystemPrompt(context: ChatContextItem[]) {
        const systemPrompt = {
            role: "system" as const,
            content: `You are an AI assistant tasked with answering questions based on the following content. Each line contains a JSON object representing either a complete media item (TV news, radio show, or podcast) or a segment from one:

Complete Media Item Format:
{
  "id": number,          // Reference ID for citations
  "type": "complete",    // Indicates this is a complete item
  "mediaType": string,   // Type of media (tv, radio, podcast)
  "title": string,      // Title of the media item
  "date": string,       // Publication date (YYYY-MM-DD)
  "text": string,       // Item description
  "segments": [         // Array of segments within this item
    {
      "index": number,  // Position in segments array (0-based)
      "title": string,  // Segment title
      "text": string,   // Segment description
      "subtitles": [    // Array of subtitles
        {
          "index": number, // Position in subtitles array (0-based)
          "text": string  // Subtitle text with optional speaker prefix
        }
      ]
    }
  ]
}

Single Segment Format:
{
  "id": number,         // Reference ID for citations
  "type": "segment",    // Indicates this is a single segment
  "mediaType": string,  // Type of media (tv, radio, podcast)
  "sourceTitle": string,// Title of the parent media item
  "date": string,       // Publication date (YYYY-MM-DD)
  "title": string,      // Segment title
  "text": string,       // Segment description
  "subtitles": [       // Array of subtitles
    {
      "index": number, // Position in subtitles array (0-based)
      "text": string  // Subtitle text with optional speaker prefix
    }
  ]
}

Content in JSONL format:

${context.map((ctx, id) => ChatState.getInstance().createContextItemJson(ctx, id)).join("\n")}

Instructions:
1. Base your answers primarily on the provided content.
2. For concepts and entities (people, organizations, places) and events mentioned in the content, you may provide general knowledge to give additional context or answer related questions. Tell the user that you are using general knowledge to answer the question, which is not supported by the content and might be wrong.
3. Keep responses focused and concise.
4. Do not make up or infer information about specific incidents or quotes not present in the content.
5. Use markdown formatting for better readability.
6. Strive to use lists and headings to make the response more readable.
7. When citing specific content, use the following format:
    - For complete items: [id]
    - For segments within complete items: [id.segmentIndex]
    - For specific subtitles in a segment: [id.segmentIndex.subtitleIndex]
    - For specific subtitles in a segment-only item: [id.subtitleIndex]
    - All indices are zero-based.
    - Do not use ranges like [3.5.12-22] or [1.1.24-26] or [0.0.79-0.0.83], just emit a citation for the first subtitile item of the range.
    - Do not emit non-sensical citations like [0.subtitleIndex 3-24]. Stick to the format specified above.`,
        };
        return systemPrompt;
    }

    calculateTotalTokens(): number {
        if (this._cachedTotalTokens !== null) {
            return this._cachedTotalTokens;
        }

        if (!this.session) return 0;

        const systemPrompt = ChatState.createSystemPrompt(this.session.context);
        const total = numTokens(systemPrompt.content);
        this._cachedTotalTokens = total;
        return total;
    }

    async complete(userMessage: string, newToken: (messages: ChatMessage[]) => void) {
        try {
            if (!this.session.id) {
                const session = await Api.session();
                if (!session || !session.id) {
                    throw new Error("Invalid session response");
                }
                this._session.id = session.id;
            }

            const systemPrompt = ChatState.createSystemPrompt(this.session.context || []);

            // For API submission, include system prompt
            const submittedMessages = [systemPrompt, ...this.session.messages.filter((m) => m.role !== "system")];
            if (
                submittedMessages[submittedMessages.length - 1].role !== "user" ||
                submittedMessages[submittedMessages.length - 1].content !== userMessage
            ) {
                submittedMessages.push({ role: "user" as const, content: userMessage });
            }

            // Update session messages, preserving any existing messages
            this.session.messages = [...submittedMessages, { role: "assistant" as const, content: "" }];

            newToken(this.session.messages);
            let currentResponse = "";

            // Convert context items to compressed format
            const compressedContext = this.session.context.map((ctx) => {
                if (ctx.type === "segment") {
                    const segmentIndex = ctx.item.segments?.findIndex((s) => s === ctx.segment) ?? -1;
                    return {
                        type: "segment" as const,
                        itemId: ctx.item.id,
                        segmentIndex,
                    };
                } else {
                    return {
                        type: "item" as const,
                        itemId: ctx.item.id,
                    };
                }
            });

            if (!this.session.id) {
                throw new Error("Session ID is required");
            }

            const tokenUsage = await Api.completeMessages(
                {
                    sessionId: this.session.id,
                    messages: submittedMessages,
                    context: compressedContext,
                },
                (chunk, done) => {
                    currentResponse += chunk;
                    this.session.messages[this.session.messages.length - 1].content = currentResponse;
                    newToken(this.session.messages);

                    if (done && this.session) {
                        this.session.lastModified = Date.now();
                    }
                }
            );

            if (tokenUsage) {
                this._tokenUsage = {
                    inputTokens: this._tokenUsage.inputTokens + tokenUsage.inputTokens,
                    outputTokens: this._tokenUsage.outputTokens + tokenUsage.outputTokens,
                };
                this.notifyListeners();
            }

            return true;
        } catch (e) {
            console.error(e);
            // Remove the empty assistant message on failure
            if (
                this.session.messages.length > 0 &&
                this.session.messages[this.session.messages.length - 1].role === "assistant" &&
                this.session.messages[this.session.messages.length - 1].content === ""
            ) {
                this.session.messages.pop();
                this.notifyListeners();
            }
            return false;
        }
    }

    public async addContextItem(context: ChatContextItem): Promise<boolean> {
        if (!this.session) return false;

        // Check if this context item already exists
        const exists = this.session.context.some((ctx) => {
            if (context.type === "segment") {
                return ctx.type === "segment" && ctx.item.id === context.item.id && ctx.segment.index === context.segment.index;
            }
            return ctx.type === "item" && ctx.item.id === context.item.id;
        });

        if (exists) return true;

        // Calculate tokens with new context item
        const newContext = [...this.session.context, context];
        const systemPrompt = ChatState.createSystemPrompt(newContext);
        let totalTokens = numTokens(systemPrompt.content);

        // Add tokens from existing messages
        this.session.messages
            .filter((m) => m.role !== "system")
            .forEach((m) => {
                totalTokens += numTokens(m.content);
            });

        // Check if adding this would exceed token limit
        if (totalTokens > 120000) {
            return false;
        }

        // Add the context item if within limits
        this.session.context.push(context);
        this._cachedTotalTokens = null; // Invalidate cache
        this.notifyListeners();
        return true;
    }

    removeContextItem(index: number) {
        if (!this.session) return;
        this.session.context = this.session.context.filter((_, i) => i !== index);
        this._cachedTotalTokens = null; // Invalidate cache
        if (this.session.context.length == 0) {
            this._session = {
                id: null,
                messages: [],
                context: [],
                lastModified: 0,
            };
        }
        this.notifyListeners();
    }

    deleteMessage(index: number) {
        if (!this.session) return;
        // Only delete non-system messages
        const systemMessageCount = this.session.messages.filter((m) => m.role === "system").length;
        const actualIndex = systemMessageCount + index;
        this.session.messages.splice(actualIndex, 1);
        this.session.lastModified = Date.now();
        this.notifyListeners();
    }

    editUserMessage(index: number, newContent: string) {
        if (!this.session) return;
        // Only edit user messages
        const systemMessageCount = this.session.messages.filter((m) => m.role === "system").length;
        const actualIndex = systemMessageCount + index;
        const message = this.session.messages[actualIndex];
        if (message && message.role === "user") {
            message.content = newContent;
            // Remove all messages after this one (including the next assistant response)
            this.session.messages = this.session.messages.slice(0, actualIndex + 1);
            this.session.lastModified = Date.now();
            this.notifyListeners();
            return true;
        }
        return false;
    }

    setSession(session: ChatSession) {
        this._session = session;
        this.notifyListeners();
    }
}
